import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, LogOut, Waves, ChefHat, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export const KitchenStaffLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('userData');
    logout();
    navigate('/staff/login');
  };

  const isActive = (path) => location.pathname === path;

  // Roles that can manage day-of menu availability
  const AVAILABILITY_ROLES = ['Bar Expeditor', 'Kitchen Expeditor', 'Supervisor'];
  const canManageAvailability = AVAILABILITY_ROLES.some(
    (r) => r.toLowerCase() === (user?.roleName || '').trim().toLowerCase()
  );

  const menuItems = [
    { name: 'Dashboard', path: '/staff/dashboard', icon: LayoutDashboard },
    { name: 'SmartView', path: '/staff/smartview', icon: Eye },
    { name: 'Orders', path: '/staff/orders', icon: ClipboardList },
    ...(canManageAvailability
      ? [{ name: 'Menu Availability', path: '/staff/menu-availability', icon: ChefHat }]
      : []),
  ];

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <div className="w-64 min-h-screen bg-slate-900 text-white flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
              <Waves className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">SmartFlags</h1>
              <p className="text-xs text-slate-400">{user?.roleName || 'Staff'}</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                      isActive(item.path)
                        ? 'bg-teal-600 text-white shadow-lg'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Info + Logout */}
        <div className="p-4 border-t border-slate-800 space-y-3">
          <div className="px-4 py-3 bg-slate-800 rounded-lg">
            <p className="text-sm font-semibold text-white">{user?.name || 'Staff'}</p>
            <p className="text-xs text-slate-400">{user?.roleName || ''}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-red-600 hover:text-white transition-all w-full"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};
